import { createFileRoute } from "@tanstack/react-router";
import type {
  ClientUserInviteCreateResponse,
  ClientUserInviteLegacyItem,
} from "api-types/userInvites/userInvitesResponses";
import type { UserAdminRole } from "api-types/users/userAdminRole";
import type { ClientUser } from "api-types/users/usersResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import { assertIsNotNullOrUndefined, assertIsNotUndefined } from "common/utils/assertOfType";
import AdminPage from "lib/components/admin/builders/AdminPage";
import InviteUserModal from "lib/components/admin/builders/UserInviteModal";
import UserInviteList from "lib/components/admin/users/UserInviteList";
import UserList from "lib/components/admin/users/UserList";
import BuilderHeader from "lib/components/builders/BuilderHeader";
import { usePaginatedFetch } from "lib/fetcher";
import { useUserContext } from "lib/state/UserContextProvider";
import {
  bulkChangeUserStatus,
  confirmUser,
  deleteUser,
  disableOrEnableUser,
  putUser,
  reconcileUserWithCognito,
  updateUserAdminRole,
} from "lib/storage/userStorage";
import { useState } from "react";
import Button from "ui/buttons/Button";
import Loading from "ui/components/Loading";
import TabbedMenu from "ui/components/TabbedMenu";
import useAppContext from "ui/hooks/useAppContext";
import useTabbedMenu from "ui/hooks/useTabbedMenu";
import IconClose from "ui/icons/IconClose";
import SearchInput from "ui/search/SearchInput";
import withAuthentication from "../../components/withAuthentication";
import styles from "./users.module.scss";

type AdminUsersTab = "users" | "invites";

const AdminUsersPage = () => {
  const { setUserNotification } = useAppContext();
  const { user: currentUser } = useUserContext();
  const {
    data: users,
    mutate: mutateUsers,
    pagination: usersPagination,
    setPagination: setUsersPagination,
    search: usersSearch,
    setSearch: setUsersSearch,
    orderBy: usersOrderBy,
    setOrderBy: setUsersOrderBy,
  } = usePaginatedFetch<ClientUser>(`${WEB_API_BASE_PATH}/users`);
  const {
    data: userInvites,
    mutate: mutateUserInvites,
    loading,
    pagination: invitesPagination,
    setPagination: setInvitesPagination,
    search: invitesSearch,
    setSearch: setInvitesSearch,
    orderBy: invitesOrderBy,
    setOrderBy: setInvitesOrderBy,
  } = usePaginatedFetch<ClientUserInviteLegacyItem>(`${WEB_API_BASE_PATH}/user-invites`);
  const [inviteModal, setInviteModal] = useState(false);
  const { tab, menuItems } = useTabbedMenu<AdminUsersTab>(["users", "invites"]);

  const handleChangeAdminRole = async (user: ClientUser, newRole: UserAdminRole) => {
    assertIsNotNullOrUndefined(user.email);

    const [updatedUser, err] = await updateUserAdminRole(user.email, newRole);

    if (err) {
      setUserNotification(err);
      return;
    }

    assertIsNotNullOrUndefined(updatedUser);
    assertIsNotUndefined(users);

    // Update the user in the list
    const updatedData = users.data.map((u) => (u.id === user.id ? updatedUser : u));
    await mutateUsers({ ...users, data: updatedData }, { revalidate: false });

    let roleLabel = "None";
    if (newRole === "SYSTEM_ADMIN") {
      roleLabel = "System Admin";
    } else if (newRole === "SUPER_ADMIN") {
      roleLabel = "Super Admin";
    }

    setUserNotification({
      message: `Updated ${user.email} to ${roleLabel}`,
      messageType: "success",
    });
  };

  const handleReconcile = async (user: ClientUser, resendIfPending?: boolean) => {
    const [result, err] = await reconcileUserWithCognito(user.id, resendIfPending);

    if (err) {
      setUserNotification(err);
      return;
    }

    assertIsNotNullOrUndefined(result);

    let message: string;
    switch (result.status) {
      case "created":
        message = `Created Cognito account and sent invite to ${user.email}`;
        break;
      case "already_exists":
        message = `User ${user.email} already exists in Cognito`;
        break;
      case "invite_resent":
        message = `Resent invite to ${user.email}`;
        break;
      default:
        message = result.message ?? `Reconciliation completed for ${user.email}`;
    }

    setUserNotification({
      message,
      messageType: "success",
    });
  };

  const handleDisableOrEnable = async (user: ClientUser, disable: boolean) => {
    const [result, err] = await disableOrEnableUser(user.id, disable);

    if (err) {
      setUserNotification(err);
      return;
    }

    assertIsNotNullOrUndefined(result);
    assertIsNotUndefined(users);

    // Update the user in the list
    const updatedData = users.data.map((u) => (u.id === user.id ? result.user : u));
    await mutateUsers({ ...users, data: updatedData }, { revalidate: false });

    setUserNotification({
      message: disable ? `Disabled ${user.email}` : `Enabled ${user.email}`,
      messageType: "success",
    });
  };

  const handleBulkChangeStatus = async (userIds: string[], disable: boolean) => {
    const [result, err] = await bulkChangeUserStatus(userIds, disable);

    if (err) {
      setUserNotification(err);
      return;
    }

    assertIsNotNullOrUndefined(result);
    assertIsNotUndefined(users);

    // Splice the updated users back into the list.
    const updatedById = new Map(result.succeeded.map((u) => [u.id, u]));
    const updatedData = users.data.map((u) => updatedById.get(u.id) ?? u);
    await mutateUsers({ ...users, data: updatedData }, { revalidate: false });

    const verb = disable ? "Disabled" : "Enabled";
    const succeededCount = result.succeeded.length;
    const failedCount = result.failed.length;
    setUserNotification({
      message:
        failedCount > 0
          ? `${verb} ${succeededCount} user${succeededCount === 1 ? "" : "s"}, ${failedCount} failed`
          : `${verb} ${succeededCount} user${succeededCount === 1 ? "" : "s"}`,
      messageType: failedCount > 0 ? "error" : "success",
    });
  };

  const handleConfirm = async (user: ClientUser) => {
    const [updatedUser, err] = await confirmUser(user.id);

    if (err) {
      setUserNotification(err);
      return;
    }

    assertIsNotNullOrUndefined(updatedUser);
    assertIsNotUndefined(users);

    // Update the user in the list
    const updatedData = users.data.map((u) => (u.id === user.id ? updatedUser : u));
    await mutateUsers({ ...users, data: updatedData }, { revalidate: false });

    setUserNotification({
      message: `Marked ${user.email} as confirmed`,
      messageType: "success",
    });
  };

  const handleDelete = async (user: ClientUser) => {
    const [result, err] = await deleteUser(user.id);

    if (err) {
      setUserNotification(err);
      return;
    }

    assertIsNotNullOrUndefined(result);
    assertIsNotUndefined(users);

    // Remove the user from the list
    const updatedData = users.data.filter((u) => u.id !== user.id);
    await mutateUsers({ ...users, data: updatedData, totalCount: users.totalCount - 1 }, { revalidate: false });

    setUserNotification({
      message: `Deleted ${user.email}`,
      messageType: "success",
    });
  };

  const handleUpdateEmailOptIn = async (user: ClientUser, optIn: boolean) => {
    const [updatedUser, err] = await putUser(user.id, { productEmailOptIn: optIn });

    if (err) {
      setUserNotification(err);
      return;
    }

    assertIsNotNullOrUndefined(updatedUser);
    assertIsNotUndefined(users);

    // Update the user in the list
    const updatedData = users.data.map((u) => (u.id === user.id ? updatedUser : u));
    await mutateUsers({ ...users, data: updatedData }, { revalidate: false });

    setUserNotification({
      message: `Updated email opt-in for ${user.email}`,
      messageType: "success",
    });
  };

  // Transform currentUser to ClientUser format for UserList
  const currentUserAsClient: ClientUser | null = currentUser
    ? {
        id: currentUser.id,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        email: currentUser.email,
        phone: currentUser.phone,
        adminRole: currentUser.adminRole,
        tempUserRole: currentUser.tempUserRole,
        featureFlags: currentUser.featureFlags,
        studioPrefs: currentUser.studioPrefs,
        productEmailOptIn: currentUser.productEmailOptIn,
        createdAt: currentUser.createdAt,
        updatedAt: currentUser.updatedAt,
        confirmedAt: currentUser.confirmedAt,
        disabledAt: currentUser.disabledAt,
        lastLoginAt: currentUser.lastLoginAt,
      }
    : null;

  const activeSearch = tab === "users" ? usersSearch : invitesSearch;
  const setActiveSearch = tab === "users" ? setUsersSearch : setInvitesSearch;

  return (
    <AdminPage currentPage="users" pageTitle="Admin - Users">
      <BuilderHeader
        header="Users"
        noBottomBorder
        noHorizontalMargin
        headerActions={
          <div className={styles.headerActions}>
            <Button key="1" btnSize="large" onClick={() => setInviteModal(true)}>
              <IconClose rotate={45} size={16} />
              <div>Invite Super Admin</div>
            </Button>
          </div>
        }
      />

      <div className={styles.searchRow}>
        <div className={styles.searchControl}>
          <SearchInput
            type="text"
            inputSize="medium"
            placeholder={tab === "users" ? "Search Users" : "Search Invites"}
            clearSearch={() => setActiveSearch("")}
            onChange={(e) => setActiveSearch(e.currentTarget.value)}
            value={activeSearch}
            isInHeader
          />
        </div>
      </div>

      <div className={styles.tabsRow}>
        <TabbedMenu menuItems={menuItems} />
      </div>

      <div className={styles.content}>
        {loading ? (
          <Loading />
        ) : (
          <>
            {tab === "users" && (
              <UserList
                users={users}
                pagination={usersPagination}
                setPagination={setUsersPagination}
                orderBy={usersOrderBy}
                setOrderBy={setUsersOrderBy}
                currentUser={currentUserAsClient}
                onChangeAdminRole={handleChangeAdminRole}
                onReconcile={handleReconcile}
                onDisableOrEnable={handleDisableOrEnable}
                onBulkChangeStatus={handleBulkChangeStatus}
                onConfirm={handleConfirm}
                onDelete={handleDelete}
                onUpdateEmailOptIn={handleUpdateEmailOptIn}
              />
            )}
            {tab === "invites" && (
              <UserInviteList
                userInvites={userInvites}
                pagination={invitesPagination}
                setPagination={setInvitesPagination}
                orderBy={invitesOrderBy}
                setOrderBy={setInvitesOrderBy}
              />
            )}
            <InviteUserModal
              onClose={() => setInviteModal(false)}
              open={inviteModal}
              onSuccess={async ({ user, userInvite }: ClientUserInviteCreateResponse) => {
                assertIsNotUndefined(users);
                assertIsNotUndefined(userInvites);

                await mutateUsers({ ...users, data: [...users?.data, user], totalCount: users.totalCount + 1 });
                await mutateUserInvites({
                  ...userInvites,
                  data: [...userInvites.data, { user, userInvite }],
                  totalCount: userInvites.totalCount + 1,
                });

                setUserNotification({ message: `Super admin invite sent to ${user.email}!`, messageType: "success" });
              }}
            />
          </>
        )}
      </div>
    </AdminPage>
  );
};

export const Route = createFileRoute("/admin/users")({
  component: withAuthentication(AdminUsersPage, { superAdmin: true }),
});
